import React, { useEffect, useMemo } from 'react';
import { AtlasState, useAtlasStore } from '../../../../store/atlasStore';
import { analyzeParallelProgress, formatParallelStatus, getJobDisplayStatus } from '../../hooks/useAnimatedProgress';
import { JobProgressBar } from '../shared/JobProgressBar';
import { SparkJob } from '../../types/sparkTypes';
import { getJobActionLabel, getJobResultSummary } from '../../utils/actionPresentation';
import { isJobRelevantToCreative } from '../../utils/jobScope';
import styles from '../../styles/EditorJobIndicator.module.css';

const MEDIA_AUTO_CLEAR_MS = 3500;

interface EditorJobIndicatorProps {
  creativeFqn: string;
  iterationFqns: string[];
  onOpenPanel: () => void;
  onOpenPanelUnfiltered: () => void;
  onNavigateToResult: (job: SparkJob) => Promise<void> | void;
  onOpenJobDetail: (jobFqn: string) => void;
  onDismissCompletion: (jobFqn: string) => Promise<void> | void;
}

const getActiveJobSummary = (job: SparkJob): string => {
  const { latestUpdate, parallelInfo } = analyzeParallelProgress(job._status_updates);
  if (parallelInfo) {
    return `${parallelInfo.stageName} (${formatParallelStatus(parallelInfo)})`;
  }
  if (latestUpdate?.message) {
    return latestUpdate.message;
  }
  return getJobDisplayStatus(job) === 'pending' ? 'Waiting to start...' : 'Processing...';
};

const getTerminalTimestamp = (job: SparkJob): number => {
  const dateString = job._completed_at || job._created_at;
  return new Date(dateString).getTime();
};

const getPrimaryActionLabel = (job: SparkJob): string => {
  if (job._status === 'failed' || job._status === 'cancelled') {
    return 'View details';
  }

  switch (job._result_kind) {
    case 'creation':
      return 'View on board';
    case 'iteration':
      return 'View iteration';
    case 'evaluation':
      return 'View evaluation';
    default:
      return 'View result';
  }
};

export const EditorJobIndicator: React.FC<EditorJobIndicatorProps> = ({
  creativeFqn,
  iterationFqns,
  onOpenPanel,
  onOpenPanelUnfiltered,
  onNavigateToResult,
  onOpenJobDetail,
  onDismissCompletion,
}) => {
  const jobs = useAtlasStore((state: AtlasState) => state.currentSparkJobs);
  const flow = useAtlasStore((state: AtlasState) => state.currentSparkFlow);

  const {
    scopedRunningJobs,
    stickyJobs,
    mediaCompletionJobs,
    othersRunningCount,
  } = useMemo(() => {
    const scope = { creativeFqn, iterationFqns };
    const scopedJobs = jobs.filter((job) => isJobRelevantToCreative(job, scope));

    return {
      scopedRunningJobs: scopedJobs
        .filter((job) => getJobDisplayStatus(job) === 'pending' || getJobDisplayStatus(job) === 'running')
        .sort((a, b) => new Date(b._created_at).getTime() - new Date(a._created_at).getTime()),
      stickyJobs: scopedJobs
        .filter((job) => {
          const displayStatus = getJobDisplayStatus(job);
          if (job._completion_seen_by_current_user) {
            return false;
          }
          if (displayStatus === 'failed' || displayStatus === 'cancelled') {
            return true;
          }
          return displayStatus === 'completed' && job._result_kind !== 'media';
        })
        .sort((a, b) => getTerminalTimestamp(a) - getTerminalTimestamp(b)),
      mediaCompletionJobs: scopedJobs
        .filter((job) =>
          !job._completion_seen_by_current_user &&
          getJobDisplayStatus(job) === 'completed' &&
          job._result_kind === 'media'
        )
        .sort((a, b) => getTerminalTimestamp(a) - getTerminalTimestamp(b)),
      othersRunningCount: jobs.filter((job) => {
        const displayStatus = getJobDisplayStatus(job);
        if (displayStatus !== 'pending' && displayStatus !== 'running') {
          return false;
        }
        return !isJobRelevantToCreative(job, scope);
      }).length,
    };
  }, [creativeFqn, iterationFqns, jobs]);

  const activeStickyJob = stickyJobs[0] || null;
  const activeMediaJob = !activeStickyJob ? mediaCompletionJobs[0] || null : null;
  const primaryRunningJob = !activeStickyJob && !activeMediaJob ? scopedRunningJobs[0] || null : null;
  const runningCount = scopedRunningJobs.length;
  const queuedStickyCount = Math.max(0, stickyJobs.length - 1);

  useEffect(() => {
    if (!activeMediaJob) {
      return;
    }

    const timer = window.setTimeout(() => {
      void Promise.resolve(onDismissCompletion(activeMediaJob.fqn));
    }, MEDIA_AUTO_CLEAR_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [activeMediaJob, onDismissCompletion]);

  if (!activeStickyJob && !activeMediaJob && !primaryRunningJob && othersRunningCount === 0) {
    return null;
  }

  const mode = activeStickyJob
    ? (activeStickyJob._status === 'failed' || activeStickyJob._status === 'cancelled' ? 'failed' : 'completed')
    : activeMediaJob
      ? 'completed'
      : primaryRunningJob
        ? 'running'
        : 'idle';

  const activeJob = activeStickyJob || activeMediaJob || primaryRunningJob;
  const additionalRunningCount = primaryRunningJob ? Math.max(0, runningCount - 1) : runningCount;

  const title = (() => {
    if (mode === 'idle') {
      return 'Project activity';
    }
    if (activeJob) {
      return getJobActionLabel(activeJob, flow);
    }
    return 'Activity';
  })();

  const summary = (() => {
    if (mode === 'idle') {
      return 'Activity is happening elsewhere in the project.';
    }
    if (mode === 'running' && activeJob) {
      return getActiveJobSummary(activeJob);
    }
    if (mode === 'failed') {
      return 'Open the job timeline for logs and failure details.';
    }
    if (activeMediaJob) {
      return 'Results are already visible inline on the form.';
    }
    return activeJob ? getJobResultSummary(activeJob, flow) : null;
  })();

  const badgeLabel = mode === 'running'
    ? 'Running'
    : mode === 'failed'
      ? 'Failed'
      : mode === 'idle'
        ? 'Project'
        : 'Ready';

  const handlePrimaryAction = async () => {
    if (!activeJob) {
      return;
    }

    if (mode === 'failed') {
      await Promise.resolve(onDismissCompletion(activeJob.fqn));
      onOpenJobDetail(activeJob.fqn);
      return;
    }

    await Promise.resolve(onDismissCompletion(activeJob.fqn));
    await Promise.resolve(onNavigateToResult(activeJob));
  };

  return (
    <div className={`${styles.indicatorCard} ${styles[`mode${mode.charAt(0).toUpperCase()}${mode.slice(1)}`]}`}>
      <button className={styles.cardSurface} onClick={onOpenPanel} type="button">
        <div className={styles.headerRow}>
          <div className={styles.title}>{title}</div>
          <span className={`${styles.statusPill} ${styles[`status${mode.charAt(0).toUpperCase()}${mode.slice(1)}`]}`}>
            {badgeLabel}
          </span>
        </div>
        <div className={styles.contentBody}>
          {summary && <div className={styles.summary}>{summary}</div>}
          {mode === 'running' && activeJob && (
            <div className={styles.progressWrap}>
              <JobProgressBar job={activeJob} variant="panel" hideStatus hideEta />
            </div>
          )}
        </div>
      </button>

      <div className={styles.footerRow}>
        {(mode === 'completed' || mode === 'failed') && activeStickyJob && (
          <>
            <button className={styles.primaryLink} onClick={() => void handlePrimaryAction()} type="button">
              {getPrimaryActionLabel(activeStickyJob)}
            </button>
            <button
              className={styles.secondaryLink}
              onClick={() => void Promise.resolve(onDismissCompletion(activeStickyJob.fqn))}
              type="button"
            >
              Dismiss
            </button>
          </>
        )}

        {mode === 'running' && additionalRunningCount > 0 && (
          <span className={styles.metaText}>+{additionalRunningCount} more running</span>
        )}

        {(mode === 'completed' || mode === 'failed') && runningCount > 0 && (
          <span className={styles.metaText}>
            {runningCount} still running
          </span>
        )}

        {queuedStickyCount > 0 && (
          <span className={styles.metaText}>+{queuedStickyCount} more waiting</span>
        )}

        {othersRunningCount > 0 && (
          <button className={styles.secondaryLink} onClick={onOpenPanelUnfiltered} type="button">
            +{othersRunningCount} others running
          </button>
        )}
      </div>
    </div>
  );
};

export default EditorJobIndicator;
